/**
 * TrackingInjector + CookieConsentBanner + CookiePreferencesPill
 *
 * Mounts globally inside <App/>. Responsibilities:
 *   1. Show a minimal "Accept / Reject" cookie banner on first visit until
 *      the user makes a choice. Choice is persisted in localStorage under
 *      `tracking_consent` ('accepted' | 'rejected').
 *   2. While consent is missing or rejected, NO tracking snippets are
 *      injected — required for GDPR / CCPA / UK PECR compliance.
 *   3. Once consent is accepted, fetch active snippets from
 *      `GET /api/tracking/snippets?surface=public|app` and inject them
 *      into <head> or end of <body> per the admin's placement choice.
 *   4. Re-fetches when the user logs in/out (surface flips public ↔ app)
 *      so app-only snippets only fire for authenticated users.
 *   5. Logs every accept/reject/withdraw event to `/api/consent/log` for
 *      the GDPR/UK PECR audit trail.
 *   6. Renders a tiny "Cookie preferences" pill after a choice is recorded
 *      so users can re-open the banner / withdraw consent at any time
 *      (required under GDPR Art. 7(3)).
 *
 * Security note: snippets are admin-curated raw HTML/JS. We render them via
 * dynamically-created <script>/<div> nodes inside try/catch so a single
 * broken snippet can't take down the page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, X, Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { publicTrackingApi, consentApi } from '../lib/api';
import { useAuth } from '../lib/auth-context';

const CONSENT_KEY = 'tracking_consent'; // 'accepted' | 'rejected'
const CLIENT_ID_KEY = 'tracking_client_id'; // opaque UUID per browser
const CONSENT_VERSION = '1.0';
const INJECTED_ATTR = 'data-ee-tracking-id';

function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) || '';
  } catch (_e) {
    return '';
  }
}

function getOrCreateClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      // crypto.randomUUID is available in all modern browsers; fallback to
      // a manually-built v4-style id if not.
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch (_e) {
    return 'no-storage';
  }
}

function writeConsent(v) {
  try {
    localStorage.setItem(CONSENT_KEY, v);
    window.dispatchEvent(new CustomEvent('tracking:consent', { detail: v }));
  } catch (_e) {
    /* ignore */
  }
}

function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('tracking:consent', { detail: '' }));
  } catch (_e) {
    /* ignore */
  }
}

/** Fire-and-forget POST to the audit log. Errors are swallowed because a
 *  failed audit log entry should never block the UX. */
function logConsentEvent(choice) {
  try {
    consentApi
      .log({
        choice,
        clientId: getOrCreateClientId(),
        pageUrl: typeof window !== 'undefined' ? window.location.href.slice(0, 1024) : '',
        consentVersion: CONSENT_VERSION,
      })
      .catch(() => {
        /* network errors are non-fatal — banner UX still proceeds */
      });
  } catch (_e) {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Banner — minimal, dismissible, accessible
// ---------------------------------------------------------------------------
export function CookieConsentBanner() {
  const [consent, setConsent] = useState(() => readConsent());

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || '');
    window.addEventListener('tracking:consent', onChange);
    return () => window.removeEventListener('tracking:consent', onChange);
  }, []);

  if (consent === 'accepted' || consent === 'rejected') return null;

  const onAccept = () => {
    writeConsent('accepted');
    setConsent('accepted');
    logConsentEvent('accepted');
  };
  const onReject = () => {
    writeConsent('rejected');
    setConsent('rejected');
    logConsentEvent('rejected');
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-3 left-3 right-3 md:left-6 md:right-auto md:max-w-md z-[300] rounded-2xl border border-border bg-background/95 backdrop-blur-md shadow-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">We use cookies</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            We use cookies and similar tools to understand how you use Blayzrr and improve
            the product. You can accept or reject analytics tracking — your choice is saved on
            this device, and your decision is logged for our compliance records.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={onAccept}
              className="rounded-xl"
              data-testid="cookie-accept-btn"
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onReject}
              className="rounded-xl"
              data-testid="cookie-reject-btn"
            >
              Reject
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={onReject}
          aria-label="Reject and close"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="cookie-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Floating "Cookie preferences" pill — shows after a choice was made so
// users can re-open the banner / withdraw consent at any time. Required
// under GDPR Art. 7(3) — consent must be as easy to withdraw as to give.
// ---------------------------------------------------------------------------
export function CookiePreferencesPill() {
  const [consent, setConsent] = useState(() => readConsent());

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || '');
    window.addEventListener('tracking:consent', onChange);
    return () => window.removeEventListener('tracking:consent', onChange);
  }, []);

  // Only render once an actual choice has been recorded.
  if (consent !== 'accepted' && consent !== 'rejected') return null;

  const onOpen = () => {
    // If the user had previously accepted, count this as an explicit
    // withdrawal in the audit log; rejected users just see the banner again.
    if (consent === 'accepted') logConsentEvent('withdrawn');
    clearConsent();
    setConsent('');
  };

  return (
    <button
      type="button"
      onClick={onOpen}
      className="fixed bottom-3 right-3 z-[200] inline-flex items-center gap-1.5 rounded-full border border-border bg-background/90 backdrop-blur-md px-3 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 shadow-md transition-colors"
      aria-label="Manage cookie preferences"
      data-testid="cookie-preferences-pill"
    >
      <Cookie size={12} />
      Cookie preferences
    </button>
  );
}

// ---------------------------------------------------------------------------
// Injector — fetches active snippets and injects them into <head> / <body>
// ---------------------------------------------------------------------------

/**
 * Parse a raw HTML snippet string (potentially containing multiple <script>
 * tags + plain HTML) into actual DOM nodes that will execute. We can't just
 * set innerHTML on a <div> because <script> tags inserted that way DO NOT
 * execute. We need to recreate each <script> node manually.
 */
function snippetToNodes(rawHtml) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = rawHtml;
  const out = [];
  // Convert NodeList -> array up front because we'll mutate the wrapper.
  const children = Array.from(wrapper.childNodes);
  for (const node of children) {
    if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SCRIPT') {
      // Recreate the script tag so the browser actually executes it.
      const s = document.createElement('script');
      for (const attr of Array.from(node.attributes)) {
        try {
          s.setAttribute(attr.name, attr.value);
        } catch (_e) {
          /* ignore bad attrs */
        }
      }
      s.text = node.textContent || '';
      out.push(s);
    } else {
      out.push(node);
    }
  }
  return out;
}

function clearInjected() {
  try {
    document.querySelectorAll(`[${INJECTED_ATTR}]`).forEach((el) => el.remove());
  } catch (_e) {
    /* ignore */
  }
}

function injectSnippets(payload) {
  if (!payload) return;
  const head = document.head;
  const body = document.body;
  // Head snippets
  for (const snip of payload.head || []) {
    try {
      const nodes = snippetToNodes(snip.code || '');
      for (const n of nodes) {
        if (n.setAttribute) n.setAttribute(INJECTED_ATTR, snip.id);
        head.appendChild(n);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[tracking] failed to inject head snippet', snip.name, e);
    }
  }
  // Body-end snippets
  for (const snip of payload.body_end || []) {
    try {
      const nodes = snippetToNodes(snip.code || '');
      for (const n of nodes) {
        if (n.setAttribute) n.setAttribute(INJECTED_ATTR, snip.id);
        body.appendChild(n);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[tracking] failed to inject body snippet', snip.name, e);
    }
  }
}

export function TrackingInjector() {
  const { user } = useAuth();
  const surface = useMemo(() => (user ? 'app' : 'public'), [user]);
  const [consent, setConsent] = useState(() => readConsent());

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || '');
    window.addEventListener('tracking:consent', onChange);
    return () => window.removeEventListener('tracking:consent', onChange);
  }, []);

  useEffect(() => {
    // Hard guard: never inject anything until the user has explicitly
    // accepted. Rejected / undecided users get zero tracking.
    if (consent !== 'accepted') {
      clearInjected();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const payload = await publicTrackingApi.list(surface);
        if (cancelled) return;
        // Clear any previously-injected snippets so a surface change
        // (login/logout) doesn't double-fire.
        clearInjected();
        injectSnippets(payload);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[tracking] could not load snippets', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [surface, consent]);

  return null;
}

export default TrackingInjector;
